import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Typography, Descriptions } from 'antd';
import { useTranslation } from 'react-i18next';

import generatePath from '~settings/routing';
import { isSupplyRole, isAdminRole, isRegionalSupplyHeadRole } from '~helpers/auth';
import SvgFormat from 'common/helpers/svgFormat';
import { formatDayDateMonthYear } from 'common/utils/date-format';
import UpdateLandlordStatusBtn from '../updateLandlordStatus';

const useStyles = makeStyles(
  theme => ({
    wrap: ({ color }) => ({
      paddingTop: 24,
      borderLeft: `8px solid ${theme.colors[color]}`,
      borderRadius: theme.borderRadius.small,
      background: theme.colors.gray1,
    }),
    landlordInfo: {
      cursor: 'pointer',
      padding: '0 24px 8px 34px',

      '& .ant-descriptions-title': {
        ...theme.typography.h4,
        color: theme.colors.titleColor,
        marginBottom: 16,
      },

      '& .ant-descriptions-item-content, .ant-descriptions-item-label': {
        color: theme.colors.bodyColor,
      },
    },
    landlordInfoLabel: {
      ...theme.typography.body2,
    },
    footer: {
      ...theme.flex.mainSpaceBetween,
      alignItems: 'center',
      padding: '8px 24px 8px 34px',
      borderTop: `1px solid ${theme.colors.gray4}`,
    },
    btn: {
      border: `1px solid ${theme.colors.mainGreen6} !important`,
    },
    footerTime: ({ color }) => ({
      color: theme.colors[color],
    }),
    svg: ({ color }) => ({
      width: 14,
      height: 14,
      marginTop: '-4px',
      marginRight: 8,
      verticalAlign: 'middle',
      fill: theme.colors[color],
    }),
  }),
  { name: 'PendingVerificationCard' },
);

const Footer = ({
  pendingAt,
  color,
  landlordId,
  landlordStatus,
  waitingDays,
  refetchLandlords,
}) => {
  const { t } = useTranslation();
  const classes = useStyles({ color });

  return (
    <div className={classes.footer}>
      <Typography className={classes.footerTime}>
        <If condition={waitingDays}>
          <SvgFormat type="clock-warning" className={classes.svg} />
          {t('waiting_for_days_onboarding_at_date', {
            days: waitingDays,
            date: formatDayDateMonthYear(pendingAt),
          })}
        </If>
      </Typography>

      <If condition={isSupplyRole() || isAdminRole() || isRegionalSupplyHeadRole()}>
        <UpdateLandlordStatusBtn
          landlordId={landlordId}
          landlordStatus={landlordStatus}
          successCallBack={refetchLandlords}
        />
      </If>
    </div>
  );
};

Footer.propTypes = {
  pendingAt: PropTypes.string,
  landlordId: PropTypes.string,
  color: PropTypes.string,
  waitingDays: PropTypes.number,
  refetchLandlords: PropTypes.func.isRequired,
};

const LandlordInfoContent = ({ landlordInfo }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();

  const handleGoToDetail = () => {
    history.push(generatePath('landlord', { landlordSlug: landlordInfo.slug }));
  };

  const landlordInfoLabels = useMemo(
    () => ({
      companyName: {
        transLabel: t('company_name'),
        value: landlordInfo?.name,
      },
      businessOpportunity: {
        transLabel: t('business_opportunity'),
        value: landlordInfo?.businessScale,
      },
      registeredBusinessNumber: {
        transLabel: t('registered_business_number'),
        value: landlordInfo?.businessNumber,
      },
      zipCode: {
        transLabel: t('zip_code'),
        value: landlordInfo?.billingPostalCode,
      },
      address: {
        transLabel: t('registered_business_address'),
        value: landlordInfo?.billingAddress,
      },
      city: {
        transLabel: t('city'),
        value: ''.concat(landlordInfo?.billingCity?.name, ', ', landlordInfo?.billingCountry?.name),
      },
    }),
    [
      t,
      landlordInfo?.name,
      landlordInfo?.businessScale,
      landlordInfo?.businessNumber,
      landlordInfo?.billingPostalCode,
      landlordInfo?.billingAddress,
      landlordInfo?.billingCity?.name,
      landlordInfo?.billingCountry?.name,
    ],
  );

  const personalInfoLabels = useMemo(
    () => ({
      companyName: {
        transLabel: t('cms.table.column.name'),
        value: `${landlordInfo?.firstName} ${landlordInfo?.lastName} `,
      },
      businessOpportunity: {
        transLabel: t('business_opportunity'),
        value: landlordInfo?.businessScale,
      },
      registeredBusinessNumber: {
        transLabel: t('cms.landlord.pending_card.government_issued'),
        value: landlordInfo?.governmentIssuedId,
      },
      address: {
        transLabel: t('cms.landlord.pending_card.address'),
        value: landlordInfo?.billingAddress,
      },

      city: {
        transLabel: t('city'),
        value: ''.concat(landlordInfo?.billingCity?.name, ', ', landlordInfo?.billingCountry?.name),
      },
      zipCode: {
        transLabel: t('zip_code'),
        value: landlordInfo?.billingPostalCode,
      },
    }),
    [
      t,
      landlordInfo?.firstName,
      landlordInfo?.lastName,
      landlordInfo?.businessScale,
      landlordInfo?.governmentIssuedId,
      landlordInfo?.billingAddress,
      landlordInfo?.businessNumber,
      landlordInfo?.billingCity?.name,
      landlordInfo?.billingCountry?.name,
      landlordInfo?.billingPostalCode,
    ],
  );

  const showLabels =
    landlordInfo?.businessType === 'COMPANY' ? landlordInfoLabels : personalInfoLabels;
  const title =
    landlordInfo?.businessType === 'COMPANY'
      ? landlordInfo?.name
      : `${landlordInfo?.firstName} ${landlordInfo?.lastName} `;
  return (
    <div className={classes.landlordInfo} onClick={handleGoToDetail}>
      <Descriptions title={title} column={2}>
        <For each="label" of={Object.keys(showLabels)}>
          <Descriptions.Item key={label} label={showLabels[label]?.transLabel}>
            {showLabels[label]?.value ?? '-'}
          </Descriptions.Item>
        </For>
      </Descriptions>
    </div>
  );
};

LandlordInfoContent.propTypes = {
  landlordInfo: PropTypes.object.isRequired,
};

const getPendingAtColorType = pendingAt => {
  const timeNow = new Date();
  const waitingDays = pendingAt ? moment(timeNow).diff(pendingAt, 'days') : 0;

  if (waitingDays >= 7) {
    return {
      colorName: 'red7',
      waitingDays: waitingDays,
    };
  }

  if (waitingDays >= 3) {
    return {
      colorName: 'orange6',
      waitingDays: waitingDays,
    };
  }

  return {
    colorName: 'gray5',
  };
};

const PendingVerificationCard = ({ landlordInfo, refetchLandlords }) => {
  const pendingAtColorType = getPendingAtColorType(landlordInfo?.pendingAt);
  const classes = useStyles({ color: pendingAtColorType.colorName });

  return (
    <div className={classes.wrap}>
      <LandlordInfoContent landlordInfo={landlordInfo} />
      <Footer
        pendingAt={landlordInfo?.pendingAt}
        landlordId={landlordInfo?.id}
        landlordStatus={landlordInfo?.status}
        color={pendingAtColorType.colorName}
        waitingDays={pendingAtColorType.waitingDays}
        refetchLandlords={refetchLandlords}
      />
    </div>
  );
};

PendingVerificationCard.propTypes = {
  landlordInfo: PropTypes.object.isRequired,
  refetchLandlords: PropTypes.func.isRequired,
};

export default PendingVerificationCard;
